import {
  VALIDATION_MESSAGES,
  WEBSITE_REGEX,
} from "../../../utils/validationPatterns";
import {
  CmpProspectFieldsFragment,
  CmpProspectFieldsInput as GeneratedCmpProspectFieldsInput,
} from "../../../graphql/types";

export type ProspectFormValues = {
  leadCaptureId: string;
  createdByName: string;
  companyName: string;
  companyWebsite: string;
  mainContactName: string;
  mainContactEmail: string;
  mainPhoneNumber: string;
  mainPhoneNumberExtension: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
};

export type ProspectFormErrors = Partial<
  Record<keyof ProspectFormValues, string>
>;

export type ProspectFieldsInput = GeneratedCmpProspectFieldsInput;

export type ProspectRecordForForm = CmpProspectFieldsFragment;

export type ProspectSaveResult = CmpProspectFieldsFragment;

export const PROSPECT_FIELD_MAX_LENGTHS = {
  leadCaptureId: 200,
  createdByName: 100,
  companyName: 200,
  companyWebsite: 2048,
  mainContactName: 200,
  mainContactEmail: 320,
  mainPhoneNumber: 32,
  mainPhoneNumberExtension: 8,
  streetAddress: 300,
  city: 100,
  province: 100,
  postalCode: 32,
  country: 100,
  leadCaptureLabel: 100,
} as const;

export const emptyProspectFormValues: ProspectFormValues = {
  leadCaptureId: "",
  createdByName: "",
  companyName: "",
  companyWebsite: "",
  mainContactName: "",
  mainContactEmail: "",
  mainPhoneNumber: "",
  mainPhoneNumberExtension: "",
  streetAddress: "",
  city: "",
  province: "",
  postalCode: "",
  country: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_ALLOWED_CHARACTERS_PATTERN = /^[\d+().\s-]+$/;
const PHONE_EXTENSION_PATTERN = /^\d{1,8}$/;

export const validateProspectForm = (
  values: ProspectFormValues,
): ProspectFormErrors => {
  const errors: ProspectFormErrors = {};

  if (!values.leadCaptureId.trim()) {
    errors.leadCaptureId = "Select a lead capture source.";
  }
  if (!values.companyName.trim()) {
    errors.companyName = "Enter a company name.";
  }
  if (
    values.companyWebsite.trim() &&
    !WEBSITE_REGEX.test(values.companyWebsite.trim())
  ) {
    errors.companyWebsite = VALIDATION_MESSAGES.WEBSITE_INVALID;
  }
  if (
    values.mainContactEmail.trim() &&
    !EMAIL_PATTERN.test(values.mainContactEmail.trim())
  ) {
    errors.mainContactEmail = "Please enter a valid email address.";
  }
  const phoneNumber = values.mainPhoneNumber.trim();
  const phoneDigitCount = (phoneNumber.match(/\d/g) || []).length;
  if (
    phoneNumber &&
    (!PHONE_ALLOWED_CHARACTERS_PATTERN.test(phoneNumber) ||
      phoneDigitCount < 7 ||
      phoneDigitCount > 15)
  ) {
    errors.mainPhoneNumber =
      "Please enter 7 to 15 digits using only numbers, spaces, +, -, (, ), or .";
  }
  if (
    values.mainPhoneNumberExtension.trim() &&
    !PHONE_EXTENSION_PATTERN.test(values.mainPhoneNumberExtension.trim())
  ) {
    errors.mainPhoneNumberExtension =
      "Please enter an extension using up to 8 digits.";
  }
  if (!values.city.trim()) {
    errors.city = "Enter a city.";
  }
  if (!values.province.trim()) {
    errors.province = "Select a province or state.";
  }
  if (!values.country.trim()) {
    errors.country = "Enter a country.";
  }

  const fieldsWithMaximumLengths: Array<{
    field: keyof ProspectFormValues;
    label: string;
    maximumLength: number;
  }> = [
    {
      field: "leadCaptureId",
      label: "Lead capture",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.leadCaptureId,
    },
    {
      field: "createdByName",
      label: "Created by",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.createdByName,
    },
    {
      field: "companyName",
      label: "Company name",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.companyName,
    },
    {
      field: "companyWebsite",
      label: "Company website",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.companyWebsite,
    },
    {
      field: "mainContactName",
      label: "Main contact name",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainContactName,
    },
    {
      field: "mainContactEmail",
      label: "Main contact email",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainContactEmail,
    },
    {
      field: "mainPhoneNumber",
      label: "Main phone",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainPhoneNumber,
    },
    {
      field: "mainPhoneNumberExtension",
      label: "Phone extension",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.mainPhoneNumberExtension,
    },
    {
      field: "streetAddress",
      label: "Street address",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.streetAddress,
    },
    {
      field: "city",
      label: "City",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.city,
    },
    {
      field: "province",
      label: "Province or state",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.province,
    },
    {
      field: "postalCode",
      label: "Postal or zip code",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.postalCode,
    },
    {
      field: "country",
      label: "Country",
      maximumLength: PROSPECT_FIELD_MAX_LENGTHS.country,
    },
  ];

  fieldsWithMaximumLengths.forEach(({ field, label, maximumLength }) => {
    if (values[field].trim().length > maximumLength) {
      errors[field] = `${label} must be ${maximumLength} characters or fewer.`;
    }
  });

  return errors;
};

const optionalTrimmedValue = (value: string): string | null => {
  const trimmedValue = value.trim();
  return trimmedValue || null;
};

export const toProspectFieldsInput = (
  values: ProspectFormValues,
): ProspectFieldsInput => ({
  leadCaptureId: values.leadCaptureId.trim(),
  createdByName: optionalTrimmedValue(values.createdByName),
  companyName: values.companyName.trim(),
  companyWebsite: optionalTrimmedValue(values.companyWebsite),
  mainContactName: optionalTrimmedValue(values.mainContactName),
  mainContactEmail: optionalTrimmedValue(values.mainContactEmail),
  mainPhoneNumber: optionalTrimmedValue(values.mainPhoneNumber),
  mainPhoneNumberExtension: optionalTrimmedValue(
    values.mainPhoneNumberExtension,
  ),
  address: {
    streetAddress: optionalTrimmedValue(values.streetAddress),
    city: values.city.trim(),
    province: values.province.trim(),
    postalCode: optionalTrimmedValue(values.postalCode),
    country: values.country.trim(),
  },
});

export const mapProspectToFormValues = (
  prospect: ProspectRecordForForm,
): ProspectFormValues => ({
  leadCaptureId: prospect.leadCaptureId,
  createdByName: prospect.createdByName || "",
  companyName: prospect.companyName,
  companyWebsite: prospect.companyWebsite || "",
  mainContactName: prospect.mainContactName || "",
  mainContactEmail: prospect.mainContactEmail || "",
  mainPhoneNumber: prospect.mainPhoneNumber || "",
  mainPhoneNumberExtension: prospect.mainPhoneNumberExtension || "",
  streetAddress: prospect.address.streetAddress || "",
  city: prospect.address.city,
  province: prospect.address.province,
  postalCode: prospect.address.postalCode || "",
  country: prospect.address.country,
});

export const prospectFormValuesMatch = (
  left: ProspectFormValues,
  right: ProspectFormValues,
): boolean =>
  (Object.keys(left) as Array<keyof ProspectFormValues>).every(
    (field) => left[field] === right[field],
  );

export const getProspectErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string =>
  error instanceof Error && error.message ? error.message : fallbackMessage;
